import React, { useState, useEffect } from 'react';
import { useUserVFS } from '@os/hooks/useUserVFS';
import { useToast } from '@os/hooks/use-toast';
import { Button } from '@os/components/ui/button';
import { Input } from '@os/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@os/components/ui/dialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@os/components/ui/alert-dialog';
import { Label } from '@os/components/ui/label';
import { Textarea } from '@os/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import { Folder, Plus, Code, Globe, Smartphone, Server, Database, Palette, Gamepad2, Cpu, Trash2 } from 'lucide-react';

interface Project {
  id: string;
  name: string;
  path: string;
  type: string;
  description: string;
  created: string;
  lastModified: string;
}

interface ProjectManagerProps {
  onProjectSelect: (project: Project) => void;
  currentProject?: Project | null;
}

const projectTemplates = [
  {
    id: 'react',
    name: 'React Application',
    description: 'Modern React app with TypeScript and Vite',
    icon: Code,
    files: {
      'package.json': JSON.stringify({
        "name": "my-react-app",
        "private": true,
        "version": "0.0.0",
        "type": "module",
        "scripts": {
          "dev": "vite",
          "build": "tsc && vite build",
          "preview": "vite preview"
        },
        "dependencies": {
          "react": "^18.2.0",
          "react-dom": "^18.2.0"
        },
        "devDependencies": {
          "@types/react": "^18.2.15",
          "@types/react-dom": "^18.2.7",
          "@vitejs/plugin-react": "^4.0.3",
          "typescript": "^5.0.2",
          "vite": "^4.4.5"
        }
      }, null, 2),
      'src/App.tsx': `import React from 'react'
import './App.css'

function App() {
  return (
    <div className="App">
      <h1>Hello React!</h1>
      <p>Welcome to your new React application.</p>
    </div>
  )
}

export default App`,
      'src/main.tsx': `import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)`,
      'src/App.css': `.App {
  text-align: center;
  padding: 2rem;
}`,
      'src/index.css': `body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}`,
      'index.html': `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>My React App</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>`,
      'README.md': `# My React App

A new React application created with Vite and TypeScript.

## Getting Started

\`\`\`bash
npm install
npm run dev
\`\`\``,
      '.gitignore': `node_modules
dist
.env.local
.env.development.local
.env.test.local
.env.production.local`
    }
  },
  {
    id: 'vanilla',
    name: 'Vanilla JavaScript',
    description: 'Simple HTML, CSS, and JavaScript project',
    icon: Globe,
    files: {
      'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>My Project</title>
    <link rel="stylesheet" href="styles.css">
</head>
<body>
    <h1>Hello World!</h1>
    <p>Welcome to your new project.</p>
    <script src="script.js"></script>
</body>
</html>`,
      'styles.css': `body {
    font-family: Arial, sans-serif;
    margin: 0;
    padding: 20px;
    background-color: #f5f5f5;
}

h1 {
    color: #333;
    text-align: center;
}`,
      'script.js': `console.log('Hello, World!');

document.addEventListener('DOMContentLoaded', function() {
    console.log('Page loaded successfully');
});`,
      'README.md': `# My Project

A simple web project with HTML, CSS, and JavaScript.`,
      '.gitignore': `.DS_Store
*.log`
    }
  },
  {
    id: 'node',
    name: 'Node.js API',
    description: 'Express.js REST API with TypeScript',
    icon: Server,
    files: {
      'package.json': JSON.stringify({
        "name": "my-api",
        "version": "1.0.0",
        "description": "A Node.js API",
        "main": "dist/index.js",
        "scripts": {
          "start": "node dist/index.js",
          "dev": "ts-node src/index.ts",
          "build": "tsc"
        },
        "dependencies": {
          "express": "^4.18.2",
          "cors": "^2.8.5"
        },
        "devDependencies": {
          "@types/express": "^4.17.17",
          "@types/cors": "^2.8.13",
          "typescript": "^5.0.0",
          "ts-node": "^10.9.0"
        }
      }, null, 2),
      'src/index.ts': `import express from 'express';
import cors from 'cors';

const app = express();
const PORT = process.env.PORT || 3000;

app.use(cors());
app.use(express.json());

app.get('/', (req, res) => {
  res.json({ message: 'Hello API!' });
});

app.listen(PORT, () => {
  console.log(\`Server running on port \${PORT}\`);
});`,
      'tsconfig.json': JSON.stringify({
        "compilerOptions": {
          "target": "ES2020",
          "module": "commonjs",
          "outDir": "./dist",
          "rootDir": "./src",
          "strict": true,
          "esModuleInterop": true,
          "skipLibCheck": true,
          "forceConsistentCasingInFileNames": true
        },
        "include": ["src/**/*"],
        "exclude": ["node_modules", "dist"]
      }, null, 2),
      'README.md': `# My API

A Node.js API built with Express and TypeScript.

## Getting Started

\`\`\`bash
npm install
npm run dev
\`\`\``,
      '.gitignore': `node_modules
dist
.env
*.log`
    }
  },
  {
    id: 'react-demo',
    name: 'React Demo Component',
    description: 'Simple React component ready to run in the browser',
    icon: Code,
    files: {
      'App.js': `// Simple React Counter Component
const { useState } = React;

function App() {
  const [count, setCount] = useState(0);
  const [name, setName] = useState('World');

  const handleIncrement = () => setCount(count + 1);
  const handleDecrement = () => setCount(count - 1);
  const handleReset = () => setCount(0);

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      maxWidth: '600px',
      margin: '50px auto',
      padding: '20px',
      textAlign: 'center',
      border: '1px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#f9f9f9'
    }}>
      <h1 style={{color: '#333', marginBottom: '30px'}} >
        Hello, {name}! 🚀
      </h1>
      
      <div style={{marginBottom: '30px'}}>
        <label style={{display: 'block', marginBottom: '10px', fontSize: '16px'}} >
          What's your name?
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            padding: '10px',
            fontSize: '16px',
            border: '2px solid #007bff',
            borderRadius: '4px',
            width: '200px'
          }}
          placeholder="Enter your name"
        />
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        border: '2px solid #007bff'
      }}>
        <h2 style={{color: '#007bff', marginBottom: '20px'}} >
          Counter: {count}
        </h2>
        
        <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
          <button
            onClick={handleDecrement}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            -1
          </button>
          
          <button
            onClick={handleReset}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reset
          </button>
          
          <button
            onClick={handleIncrement}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            +1
          </button>
        </div>
      </div>

      <p style={{marginTop: '30px', color: '#666', fontSize: '14px'}} >
        This React component is running live in your MapleOS! 🎉
      </p>
    </div>
  );
}

// Export the component
exports.default = App;`,
      'README.md': `# React Demo Component

This is a simple React component that demonstrates:
- State management with useState
- Event handling
- Inline styling
- Component export for the React Runner

## How to Run

1. Open this file in the Code Editor
2. Click "Run" in the toolbar or press F5
3. Watch your React component come to life!

The component includes:
- A counter that can be incremented, decremented, and reset
- A name input field with real-time updates
- Styled with inline CSS for simplicity

Perfect for testing the React Runner functionality in MapleOS!`
    }
  },
  {
    id: 'cpp-game',
    name: 'C++ WASM-4 Game',
    description: 'Simple C++ game for WASM-4 fantasy console',
    icon: Gamepad2,
    files: {
      'main.c': `#include <stdint.h>
#include <wasm4.h>

// Game state
static int playerX = 76;
static int playerY = 76;
static int enemyX = 120;
static int enemyY = 120;
static int score = 0;
static int gameTime = 0;

// Colors (WASM-4 palette)
#define COLOR_BLACK 0
#define COLOR_WHITE 1
#define COLOR_RED   2
#define COLOR_GREEN 3

void start() {
    // Initialize game
    playerX = 76;
    playerY = 76;
    enemyX = 120;
    enemyY = 120;
    score = 0;
    gameTime = 0;
}

void update() {
    gameTime++;
    
    // Clear screen
    *DRAW_COLORS = COLOR_BLACK;
    rect(0, 0, 160, 160);
    
    // Handle input
    if (*GAMEPAD1 & BUTTON_LEFT && playerX > 0) {
        playerX -= 2;
    }
    if (*GAMEPAD1 & BUTTON_RIGHT && playerX < 152) {
        playerX += 2;
    }
    if (*GAMEPAD1 & BUTTON_UP && playerY > 0) {
        playerY -= 2;
    }
    if (*GAMEPAD1 & BUTTON_DOWN && playerY < 152) {
        playerY += 2;
    }
    
    // Move enemy towards player
    if (enemyX < playerX) enemyX++;
    if (enemyX > playerX) enemyX--;
    if (enemyY < playerY) enemyY++;
    if (enemyY > playerY) enemyY--;
    
    // Check collision
    if (abs(playerX - enemyX) < 8 && abs(playerY - enemyY) < 8) {
        // Reset enemy position
        enemyX = (gameTime * 13) % 140 + 10;
        enemyY = (gameTime * 17) % 140 + 10;
        score++;
        
        // Play tone
        tone(220, 10, 50, TONE_PULSE1);
    }
    
    // Draw player (green square)
    *DRAW_COLORS = COLOR_GREEN;
    rect(playerX, playerY, 8, 8);
    
    // Draw enemy (red square)
    *DRAW_COLORS = COLOR_RED;
    rect(enemyX, enemyY, 8, 8);
    
    // Draw score
    *DRAW_COLORS = COLOR_WHITE;
    char scoreText[32];
    snprintf(scoreText, sizeof(scoreText), "Score: %d", score);
    text(scoreText, 10, 10);
    
    // Draw instructions
    text("Arrow keys to move", 10, 140);
    text("Catch the red square!", 10, 150);
}

// Simple absolute value function
int abs(int x) {
    return x < 0 ? -x : x;
}`,
      'wasm4.h': `// WASM-4 API Header
#pragma once

#include <stdint.h>

// Screen size
#define SCREEN_SIZE 160

// Input buttons
#define BUTTON_1    1
#define BUTTON_2    2
#define BUTTON_LEFT  16
#define BUTTON_RIGHT 32
#define BUTTON_UP    64
#define BUTTON_DOWN  128

// Tone types
#define TONE_PULSE1  0
#define TONE_PULSE2  1
#define TONE_TRIANGLE 2
#define TONE_NOISE   3

// Memory mapped I/O
extern uint8_t* const PALETTE;
extern uint16_t* const DRAW_COLORS;
extern uint8_t* const GAMEPAD1;
extern uint8_t* const GAMEPAD2;
extern uint8_t* const GAMEPAD3;
extern uint8_t* const GAMEPAD4;
extern uint8_t* const MOUSE_X;
extern uint8_t* const MOUSE_Y;
extern uint8_t* const MOUSE_BUTTONS;

// Drawing functions
void rect(int x, int y, int width, int height);
void oval(int x, int y, int width, int height);
void line(int x1, int y1, int x2, int y2);
void text(const char* str, int x, int y);
void blit(const uint8_t* sprite, int x, int y, int width, int height, int flags);

// Sound function
void tone(int frequency, int duration, int volume, int flags);

// System functions
void trace(const char* str);`,
      'README.md': `# C++ WASM-4 Game

A simple C++ game that compiles to WebAssembly and runs in the WASM-4 fantasy console!

## Game Description

This is a simple "catch the square" game where:
- You control a green square with arrow keys
- Try to catch the red enemy square
- Each catch increases your score
- The enemy follows you around the screen

## Controls

- **Arrow Keys**: Move your player
- **Goal**: Catch the red square to score points!

## WASM-4 Features Used

- **Graphics**: Drawing rectangles and text
- **Input**: Reading gamepad state
- **Sound**: Playing tones when scoring
- **Memory**: Managing game state

## How to Run

1. Open this file in the Code Editor
2. Press F6 or Run → Run C++ Project
3. Click "Compile & Run" in the WASM-4 console
4. Use arrow keys to play!

This demonstrates the power of compiling C++ to WebAssembly right in your browser-based IDE!`
    }
  },
  {
    id: 'cpp-hello',
    name: 'C++ Hello World',
    description: 'Simple C++ program for WASM-4',
    icon: Code,
    files: {
      'main.c': `#include <stdint.h>
#include <wasm4.h>

static int frame = 0;
static int x = 10;
static int y = 10;

void start() {
    // Game initialization
    frame = 0;
    x = 10;
    y = 10;
}

void update() {
    frame++;
    
    // Clear screen to black
    *DRAW_COLORS = 1; // Black background
    rect(0, 0, 160, 160);
    
    // Set text color to white
    *DRAW_COLORS = 2;
    
    // Hello World message
    text("Hello from C++!", 20, 30);
    text("Running in WASM-4!", 15, 50);
    
    // Animated element
    int bounceX = x + (int)(20 * cos(frame * 0.1));
    int bounceY = y + (int)(10 * sin(frame * 0.05));
    
    // Draw a moving square
    *DRAW_COLORS = 3; // Green
    rect(bounceX, bounceY + 70, 8, 8);
    
    // Frame counter
    char frameText[32];
    snprintf(frameText, sizeof(frameText), "Frame: %d", frame);
    *DRAW_COLORS = 2; // White text
    text(frameText, 10, 120);
    
    // Instructions
    text("C++ + WebAssembly", 10, 140);
    text("= Amazing!", 10, 150);
}

// Math functions (simplified)
double cos(double x) {
    // Simple cosine approximation
    while (x > 3.14159) x -= 6.28318;
    while (x < -3.14159) x += 6.28318;
    return 1.0 - (x*x)/2.0 + (x*x*x*x)/24.0;
}

double sin(double x) {
    // Simple sine approximation  
    while (x > 3.14159) x -= 6.28318;
    while (x < -3.14159) x += 6.28318;
    return x - (x*x*x)/6.0 + (x*x*x*x*x)/120.0;
}`,
      'wasm4.h': `// WASM-4 API Header
#pragma once

#include <stdint.h>

// Screen size
#define SCREEN_SIZE 160

// Memory mapped I/O
extern uint8_t* const PALETTE;
extern uint16_t* const DRAW_COLORS;
extern uint8_t* const GAMEPAD1;

// Drawing functions  
void rect(int x, int y, int width, int height);
void text(const char* str, int x, int y);
void trace(const char* str);`,
      'README.md': `# C++ Hello World for WASM-4

A simple "Hello World" program written in C++ that demonstrates:

- Text rendering in WASM-4
- Basic animation with math functions
- Frame counting and display
- Simple graphics drawing

## Features

- Animated bouncing square using trigonometry
- Real-time frame counter
- Multiple text messages
- Clean, simple C++ code

## How it Works

This C++ code compiles to WebAssembly and runs in the WASM-4 fantasy console. The \`update()\` function is called 60 times per second, creating smooth animation.

Perfect starting point for learning C++ game development with WebAssembly!`
    }
  },
  // NEW: MapleOS Web Module template
  {
    id: 'maple-module-web',
    name: 'MapleOS Web Module',
    description: 'TS/JS web module with manifest, permissions, categories, and optional AI capability',
    icon: Globe,
    files: {
      'manifest.json': JSON.stringify({
        name: "My Web Module",
        version: "0.1.0",
        description: "A MapleOS web module.",
        author: "Your Name",
        categories: ["productivity", "utilities"],
        permissions: ["windows:open", "notifications:show", "files:read"],
        aiCapability: true,
        mapleOSVersion: ">=1.0.0",
        type: "web",
        entry: { html: "src/index.html" },
        icons: { small: "assets/icon.png" }
      }, null, 2),
      'src/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>My Web Module</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    body { font-family: system-ui, -apple-system, Segoe UI, Roboto; margin: 0; padding: 24px; }
    .box { border: 1px solid #ddd; border-radius: 8px; padding: 16px; }
    button { padding: 8px 12px; border-radius: 6px; border: 1px solid #007bff; background: #007bff; color: white; }
  </style>
</head>
<body>
  <div class="box">
    <h1>Hello MapleOS!</h1>
    <p>This is a web module running inside MapleOS Module Host.</p>
    <button onclick="notify()">Show Notification</button>
  </div>
  <script>
    // Host bridge example (install-time permissions required)
    function notify() {
      // In v1 demo, we simply show an in-page message. In phase 2, call host API via postMessage.
      alert('Module notification demo');
    }
  </script>
</body>
</html>`,
      'assets/icon.png': '',
      'README.md': `# MapleOS Web Module

This template includes:
- manifest.json with permissions, categories, and optional AI capability (MaplePT)
- src/index.html entry file rendered by ModuleHost via iframe srcDoc

Build & Run:
- Use the Code Editor Run menu → Run Module (F7) to preview and iterate.

Install:
- Use ModuleHost to install to Program Files (admin-gated in future phases).`
    }
  },
  // NEW: MapleOS AssemblyScript Module template
  {
    id: 'maple-module-as',
    name: 'MapleOS AssemblyScript Module',
    description: 'AssemblyScript module with manifest and WASM entry',
    icon: Cpu,
    files: {
      'manifest.json': JSON.stringify({
        name: "My WASM Module",
        version: "0.1.0",
        description: "A MapleOS AssemblyScript module.",
        author: "Your Name",
        categories: ["graphics", "utilities"],
        permissions: ["windows:open", "files:read"],
        aiCapability: false,
        mapleOSVersion: ">=1.0.0",
        type: "wasm",
        entry: { wasm: "build/module.wasm" },
        icons: { small: "assets/icon.png" }
      }, null, 2),
      'src/main.ts': `// AssemblyScript source (compile to build/module.wasm)
// For v1, a runnable WASM is included via WAT in build/module.wasm.
// You can later replace it with your own compiled output.
export function add(a: i32, b: i32): i32 {
  return a + b;
}`,
      // Ready-to-run WAT that Module Host compiles to WASM on Run
      'build/module.wasm': `(module
  (func $add (param i32 i32) (result i32)
    local.get 0
    local.get 1
    i32.add)
  (export "add" (func $add))
)`,
      'assets/icon.png': '',
      'README.md': `# MapleOS AssemblyScript Module

This template includes:
- manifest.json with permissions, categories, and optional AI capability
- src/main.ts (source idea)
- build/module.wasm containing WAT that Module Host compiles to WASM on Run

Run:
- Open the project in Code Editor, press F7 (Run Module), then click Run in Module Host.
- The host will compile the WAT and show exports (e.g., add).`
    }
  }
];

export const ProjectManager: React.FC<ProjectManagerProps> = ({ onProjectSelect, currentProject }) => {
  const { createFile, listFiles, files, getFile, fileExists, deleteFile, updateFile } = useUserVFS();
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    template: 'maple-module-as' // Default to AssemblyScript module
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load existing projects
  useEffect(() => {
    const loadProjects = () => {
      const projectFiles = files.filter(file => 
        file.file_name === '.project' && file.is_directory === false
      );
      
      const projectsList = projectFiles.map(file => {
        try {
          const projectData = JSON.parse(file.content || '{}');
          return {
            id: projectData.id,
            name: projectData.name,
            path: file.parent_path || '',
            type: projectData.type,
            description: projectData.description,
            created: projectData.created,
            lastModified: file.updated_at
          };
        } catch {
          return null;
        }
      }).filter(Boolean) as Project[];

      setProjects(projectsList);
    };

    loadProjects();
  }, [files]);

  const createProject = async () => {
    if (!newProject.name.trim()) {
      toast({
        title: "Error",
        description: "Project name is required",
        variant: "destructive"
      });
      return;
    }

    const projectId = `project-${Date.now()}`;
    const projectPath = `C:\\Projects\\${newProject.name}`;
    const template = projectTemplates.find(t => t.id === newProject.template);

    try {
      // Create project directory (safe)
      if (!fileExists(projectPath)) {
        await createFile(projectPath, '', true);
      }

      // Create project metadata file
      const projectMetadata = {
        id: projectId,
        name: newProject.name,
        type: template?.name || 'Custom',
        description: newProject.description,
        created: new Date().toISOString(),
        template: newProject.template
      };

      const projectFilePath = `${projectPath}\\.project`;
      if (!fileExists(projectFilePath)) {
        await createFile(
          projectFilePath,
          JSON.stringify(projectMetadata, null, 2),
          false,
          'application/json'
        );
      }
      // Always ensure .project has JSON content
      await updateFile(projectFilePath, JSON.stringify(projectMetadata, null, 2));

      // Create template files
      if (template) {
        const getContentType = (p: string) => {
          const ext = p.toLowerCase().split('.').pop() || '';
          switch (ext) {
            case 'json': return 'application/json';
            case 'md': return 'text/markdown';
            case 'ts': return 'text/typescript';
            case 'tsx': return 'text/tsx';
            case 'js': return 'application/javascript';
            case 'html': return 'text/html';
            case 'css': return 'text/css';
            case 'wasm': return 'application/wat'; // WAT text stored under .wasm in template
            case 'png': return 'image/png';
            case 'ico': return 'image/x-icon';
            default: return 'text/plain';
          }
        };

        for (const [filePath, content] of Object.entries(template.files)) {
          // Normalize to Windows-style backslashes
          const normalizedFilePath = filePath.replace(/\//g, '\\');
          const fullPath = `${projectPath}\\${normalizedFilePath}`;

          // Create directories if needed (safe: only if missing)
          const pathParts = normalizedFilePath.split('\\');
          if (pathParts.length > 1) {
            let currentPath = projectPath;
            for (let i = 0; i < pathParts.length - 1; i++) {
              currentPath += `\\${pathParts[i]}`;
              if (!fileExists(currentPath)) {
                await createFile(currentPath, '', true);
              }
            }
          }

          const ct = getContentType(normalizedFilePath);

          // Create if missing; if present but empty, overwrite to ensure content
          if (!fileExists(fullPath)) {
            const ok = await createFile(fullPath, content as string, false, ct);
            if (!ok) {
              throw new Error(`Failed to create ${normalizedFilePath}`);
            }
          } else {
            const existing = getFile(fullPath);
            const existingContent = existing?.content ?? '';
            if (existingContent.trim().length === 0) {
              await updateFile(fullPath, content as string);
            }
          }
        }
      }

      // Post-create verification and auto-fix layout
      // Ensure required directories exist
      if (!fileExists(`${projectPath}\\src`)) {
        await createFile(`${projectPath}\\src`, '', true);
      }
      if (!fileExists(`${projectPath}\\build`)) {
        await createFile(`${projectPath}\\build`, '', true);
      }

      // Move misplaced root files out of src if needed
      for (const rootName of ['manifest.json', 'README.md', '.project']) {
        const rootPath = `${projectPath}\\${rootName}`;
        const misplaced = `${projectPath}\\src\\${rootName}`;
        if (fileExists(misplaced)) {
          const mf = getFile(misplaced);
          const content = mf?.content || '';
          const ct = mf?.content_type || (rootName.endsWith('.json') ? 'application/json' : 'text/plain');

          // If root exists but is empty, or doesn't exist, write to root
          if (!fileExists(rootPath) || (getFile(rootPath)?.content?.trim?.() === '')) {
            await createFile(rootPath, content, false, ct);
          } else if ((getFile(rootPath)?.content || '').trim().length === 0) {
            await updateFile(rootPath, content);
          }

          // Remove misplaced copy
          await deleteFile(misplaced);
        }
      }

      // Ensure src/main.ts exists and has sample code for AssemblyScript template
      const srcMainPath = `${projectPath}\\src\\main.ts`;
      const defaultMain = (template?.files?.['src/main.ts'] as string) ??
`// AssemblyScript sample
export function add(a: i32, b: i32): i32 { return a + b; }`;
      if (!fileExists(srcMainPath)) {
        await createFile(srcMainPath, defaultMain, false, 'text/typescript');
      } else {
        const existingMain = getFile(srcMainPath)?.content || '';
        if (!existingMain.trim()) {
          await updateFile(srcMainPath, defaultMain);
        }
      }

      // Ensure build/module.wasm (WAT text) exists (and fill if empty)
      const buildWasmPath = `${projectPath}\\build\\module.wasm`;
      const defaultWasmWat = (template?.files?.['build/module.wasm'] as string) ??
`(module
  (func $add (param i32 i32) (result i32)
    local.get 0
    local.get 1
    i32.add)
  (export "add" (func $add))
)`;
      if (!fileExists(buildWasmPath)) {
        await createFile(buildWasmPath, defaultWasmWat, false, 'application/wat');
      } else {
        const existingWat = getFile(buildWasmPath)?.content || '';
        if (!existingWat.trim()) {
          await updateFile(buildWasmPath, defaultWasmWat);
        }
      }

      // Ensure manifest.json exists at root (and fill if empty), using project name/description
      const manifestPath = `${projectPath}\\manifest.json`;
      // Start from template manifest if present, else create a sensible default
      let manifestObj: any;
      const templateManifestStr = template?.files?.['manifest.json'] as string | undefined;
      try {
        manifestObj = templateManifestStr ? JSON.parse(templateManifestStr) : {
          name: newProject.name || 'My WASM Module',
          version: '0.1.0',
          description: newProject.description || 'A MapleOS module.',
          author: 'Your Name',
          categories: ['utilities'],
          permissions: ['windows:open', 'files:read'],
          aiCapability: false,
          mapleOSVersion: '>=1.0.0',
          type: newProject.template === 'maple-module-web' ? 'web' : 'wasm',
          entry: newProject.template === 'maple-module-web'
            ? { html: 'src/index.html' }
            : { wasm: 'build/module.wasm' },
          icons: { small: 'assets/icon.png' }
        };
      } catch {
        manifestObj = {
          name: newProject.name || 'My Module',
          version: '0.1.0',
          description: newProject.description || '',
          type: 'wasm',
          entry: { wasm: 'build/module.wasm' }
        };
      }
      // Override name/description with user-provided values
      manifestObj.name = newProject.name || manifestObj.name;
      if (newProject.description) {
        manifestObj.description = newProject.description;
      }
      const manifestContent = JSON.stringify(manifestObj, null, 2);
      if (!fileExists(manifestPath)) {
        await createFile(manifestPath, manifestContent, false, 'application/json');
      } else {
        const existingManifest = getFile(manifestPath)?.content || '';
        if (!existingManifest.trim()) {
          await updateFile(manifestPath, manifestContent);
        }
      }

      // Add to projects list
      const newProjectObj: Project = {
        id: projectId,
        name: newProject.name,
        path: projectPath,
        type: template?.name || 'Custom',
        description: newProject.description,
        created: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };

      setProjects(prev => [...prev, newProjectObj]);
      onProjectSelect(newProjectObj);

      toast({
        title: "Project Created",
        description: `${newProject.name} has been created successfully`
      });

      setShowCreateDialog(false);
      setNewProject({ name: '', description: '', template: 'maple-module-as' });

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create project",
        variant: "destructive"
      });
    }
  };

  const requestDelete = (project: Project) => {
    setProjectToDelete(project);
    setDeleteOpen(true);
  };

  const performDeleteProject = async () => {
    if (!projectToDelete) return;
    setIsDeleting(true);
    try {
      const base = projectToDelete.path.replace(/\//g, '\\');
      // Collect all files/dirs under this project (including root dir)
      const affected = files
        .filter(f => f.file_path === base || f.file_path.startsWith(base + '\\'))
        .sort((a, b) => b.file_path.length - a.file_path.length); // delete children first

      let failures = 0;
      for (const f of affected) {
        const ok = await deleteFile(f.file_path);
        if (!ok) failures++;
      }

      if (failures === 0) {
        setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
        // If the deleted project is currently selected, let the user pick another (do not auto-switch)
        if (currentProject?.id === projectToDelete.id) {
          toast({
            title: 'Project deleted',
            description: `${projectToDelete.name} was removed. Select another project to continue.`
          });
        } else {
          toast({ title: 'Project deleted', description: `${projectToDelete.name} was removed.` });
        }
      } else {
        toast({
          title: 'Delete incomplete',
          description: 'Some files could not be removed. Please try again or refresh.',
          variant: 'destructive'
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete project.',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
      setProjectToDelete(null);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">Projects</h2>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] z-[9999]">
            <DialogHeader>
              <DialogTitle>Create New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  value={newProject.name}
                  onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="my-awesome-project"
                />
              </div>
              
              <div>
                <Label htmlFor="project-description">Description</Label>
                <Textarea
                  id="project-description"
                  value={newProject.description}
                  onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="A brief description of your project"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="project-template">Template</Label>
                <Select value={newProject.template} onValueChange={(value) => setNewProject(prev => ({ ...prev, template: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    {projectTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <template.icon className="w-4 h-4" />
                          <div>
                            <div className="font-medium">{template.name}</div>
                            <div className="text-sm text-muted-foreground">{template.description}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={createProject}>
                  Create Project
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {currentProject && (
          <Card className="mb-4 border-primary">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-primary">Current Project</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Folder className="w-5 h-5 text-primary" />
                <div>
                  <div className="font-medium">{currentProject.name}</div>
                  <div className="text-sm text-muted-foreground">{currentProject.type}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {projects.map((project) => (
            <Card 
              key={project.id} 
              className={`cursor-pointer transition-colors hover:bg-accent ${
                currentProject?.id === project.id ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => onProjectSelect(project)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Folder className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">{project.name}</div>
                    <div className="text-sm text-muted-foreground">{project.type}</div>
                    {project.description && (
                      <div className="text-sm text-muted-foreground mt-1">{project.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-muted-foreground">
                      {new Date(project.lastModified).toLocaleDateString()}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDelete(project);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            <Folder className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No projects yet</p>
            <p className="text-sm">Create your first project to get started</p>
          </div>
        )}
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="z-[9999]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              {projectToDelete
                ? `This will permanently remove "${projectToDelete.name}" and all files under ${projectToDelete.path}.`
                : 'This will permanently remove the selected project and all its files.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDeleteProject}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};